/**
 * Open an external URL correctly on every platform.
 *
 * In a WKWebView, navigating the app's own web view to a foreign https URL
 * (e.g. a Stripe Checkout page) would replace the app UI with no way back, and
 * `window.open(url, '_blank')` opens a dead blank view. On native we instead
 * present the URL in the in-app SafariViewController via the Browser plugin.
 * On the web we keep the familiar behaviour.
 */

import { Browser } from '@capacitor/browser';
import { isNativePlatform } from './platform';

interface OpenExternalOptions {
  /**
   * Web-only: if true, replace the current tab (`location.href`) instead of
   * opening a new one. Use for full-page redirect flows like Stripe Checkout.
   * Ignored on native (always an in-app browser sheet).
   */
  redirect?: boolean;
}

export async function openExternal(url: string, opts: OpenExternalOptions = {}): Promise<void> {
  if (isNativePlatform()) {
    await Browser.open({ url });
    return;
  }
  if (opts.redirect) {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
